import { useEffect, useMemo, useRef, useState } from 'react'
import { FeatureDemo } from 'components/feature-demo/FeatureDemo'
import {
  featureDemoStorageId,
  isFeatureDemoForcedOn,
  shouldShowFeatureDemo,
  writeFeatureDemoDismissed,
  type FeatureDemoChoice,
} from 'components/feature-demo/featureDemoStorage'
import { COMMAND_PALETTE_DEMO_ID } from 'components/command-palette/CommandPaletteOnboarding'
import { FEATURE_FLAGS } from '@/config/featureFlags'
import { useAuth } from 'hooks/auth/useAuth'
import { isPortalAssistantDemoAudience } from './portalAssistantDemoAudience'
import { readAssistantDemoDismissed, writeAssistantDemoDismissed } from './writeAssistantDemoDismiss'

export const PORTAL_ASSISTANT_DEMO_ID = 'portal-assistant-tour'

export const ASSISTANT_DEMO_EVENT = 'otw-assistant-demo'

export type AssistantDemoAnchor =
  | 'composer'
  | 'thread'
  | 'chats'
  | 'mode'
  | 'stage'
  | 'share'
  | 'settings'
  | 'portal'
  | 'preview'

export interface AssistantDemoDetail {
  history?: boolean
  workspace?: boolean
  phonePane?: 'work' | 'chat'
  /** Tour is on screen, including the intro card. Outstanding actions stay hidden. */
  active?: boolean
  /** Example preview card while those steps are on screen. */
  samplePreview?: 'thread' | 'stage' | false
  exit?: boolean
}

const INTRO = {
  title: 'Ask the Portal Assistant',
    desktopBody:
    'The sparkle opens a chat beside the page. Show Me walks through asking, confirm cards, chats, files, charts, preview cards, Assistant mode, share, and settings.',
  mobileBody:
    'The sparkle opens a chat over the page. Show Me walks through asking, confirm cards, chats, files, charts, preview cards, Assistant mode, share, and settings.',
}

const DEMO_STEPS: ReadonlyArray<{
  title: string
  body: string
  mobileBody?: string
  anchor: AssistantDemoAnchor
  history?: boolean
  workspace?: boolean
  phonePane?: 'work' | 'chat'
  samplePreview?: 'thread' | 'stage'
}> = [
  {
    title: 'Ask in Plain Language',
    body: 'Ask about inventory, leads, Gray Book prices, sales, hours, spend, shops, shipping, a truck timeline, or a page. An empty chat offers starters. Type here and send.',
    anchor: 'composer',
  },
  {
    title: 'Nothing Writes Until Yes',
    body: 'Leads, status changes, prices, lists, campaigns, and clock-in show a confirm card first. Yes saves as you. Cancel leaves the record alone.',
    anchor: 'thread',
  },
  {
    title: 'Your Chats Stay Private',
    body: 'Chats lists your private threads. Assistants is a named assistant you can share with one other admin, with routines that email on the days and times you pick.',
    anchor: 'chats',
    history: true,
  },
  {
    title: 'Photos, Invoices, and Attachments',
    body: 'Ask to see repair photos, invoices, or receipts. Open a file to preview it. The page fits in the frame so you see the whole document.',
    anchor: 'thread',
  },
  {
    title: 'Charts and Tables',
    body: 'Ranked lists and numeric breakdowns can show as a chart or a table. Under Tools in the gear, turn on the chart types you want. The assistant picks one and offers the others as buttons.',
    anchor: 'thread',
  },
  {
    title: 'Ask for a Preview Card',
    body: 'Ask for a truck, a lead, an application, or a page. Try “show stock 1042,” “preview this lead,” or “open the inventory page,” then send. The reply adds a card like the example. You do not build the card yourself.',
    anchor: 'composer',
    samplePreview: 'thread',
  },
  {
    title: 'What the Card Shows',
    body: 'The example is an inventory card: kind, name, a short line, and facts such as status, price, and miles. A photo appears when the record has one. Open, at the top right, leaves this chat and opens that portal page.',
    mobileBody:
      'The example shows the kind, name, and a few facts. Open, at the top right, leaves this chat and opens that page.',
    anchor: 'preview',
    samplePreview: 'thread',
  },
  {
    title: 'Assistant Mode',
    body: 'This opens the full-screen workspace. Charts, tables, and previews sit beside the chat. On a phone, Work and Chat switch between them.',
    mobileBody: 'This opens the full-screen workspace. Work shows results. Chat shows the conversation.',
    anchor: 'mode',
  },
  {
    title: 'Work Side',
    body: 'Charts, tables, and previews stay on this side as you keep asking. Scroll the list, pin one to keep it, and Clear removes the rest. Hide closes this side. Drag the bar to resize.',
    mobileBody: 'Charts, tables, and previews stay on Work as you keep asking. Scroll the list, pin one to keep it, and Clear removes the rest. Hide switches back to Chat.',
    anchor: 'stage',
    workspace: true,
    phonePane: 'work',
  },
  {
    title: 'Open Keeps the Page',
    body: 'Open on the example uses a live portal path. In Assistant mode it leaves the workspace and opens that page. In the drawer, the gear can also close the chat after the link.',
    anchor: 'preview',
    workspace: true,
    phonePane: 'work',
    samplePreview: 'stage',
  },
  {
    title: 'Share',
    body: 'Share downloads this chat as PDF, Markdown, or TXT, or creates a link. The link is public. A passcode is required unless you confirm there is no sensitive information. Send the code separately.',
    anchor: 'share',
    workspace: true,
    phonePane: 'work',
  },
  {
    title: 'Gear',
    body: 'The gear sets effort, how many records to search, and the date range. You can also type a range in chat. Under Tools, turn on the chart types you want. Learn from chats is on, so useful facts from earlier chats are saved. View memory lists them. You can delete one note or reset all of them. You can still say “remember …” to save a note. It can show debug notes, can close the drawer after a link, and can clear chat history.',
    anchor: 'settings',
    workspace: true,
    phonePane: 'work',
  },
  {
    title: 'Back to the Portal',
    body: 'Portal leaves Assistant mode and returns to the page you opened it from.',
    anchor: 'portal',
    workspace: true,
    phonePane: 'work',
  },
]

interface PortalAssistantOnboardingProps {
  anchorEl: HTMLElement | null
  isMobile?: boolean
  /** Command palette tour is still up, so this one waits. */
  defer?: boolean
  onOpenAssistant: () => void
  onCloseAssistant: () => void
}

export function PortalAssistantOnboarding({
  anchorEl,
  isMobile = false,
  defer = false,
  onOpenAssistant,
  onCloseAssistant,
}: PortalAssistantOnboardingProps) {
  const { currentUser, userInfo } = useAuth()
  const uid = currentUser?.uid || userInfo?.id
  const inAudience =
    isPortalAssistantDemoAudience(userInfo?.email) || isFeatureDemoForcedOn()
  const enabled = FEATURE_FLAGS.portalAssistantOnboarding && !defer && inAudience
  const storageId = featureDemoStorageId(PORTAL_ASSISTANT_DEMO_ID, uid)
  const paletteStorageId = featureDemoStorageId(COMMAND_PALETTE_DEMO_ID, uid)
  const [ready, setReady] = useState(false)
  const [visible, setVisible] = useState(false)
  const [demoIndex, setDemoIndex] = useState<number | null>(null)
  const [stepAnchor, setStepAnchor] = useState<HTMLElement | null>(null)
  const openedByDemo = useRef(false)

  useEffect(() => {
    const id = window.setTimeout(() => setReady(true), 400)
    return () => window.clearTimeout(id)
  }, [])

  useEffect(() => {
    if (!visible) return undefined
    window.dispatchEvent(
      new CustomEvent<AssistantDemoDetail>(ASSISTANT_DEMO_EVENT, { detail: { active: true } }),
    )
    return () => {
      window.dispatchEvent(
        new CustomEvent<AssistantDemoDetail>(ASSISTANT_DEMO_EVENT, { detail: { active: false } }),
      )
    }
  }, [visible])

  useEffect(() => {
    if (!inAudience) {
      setVisible(false)
      return undefined
    }
    if (!ready || !anchorEl || !uid) return undefined
    let cancelled = false
    void (async () => {
      let dismissed = false
      try {
        dismissed = await readAssistantDemoDismissed(uid, PORTAL_ASSISTANT_DEMO_ID)
      } catch {
        dismissed = false
      }
      if (cancelled) return
      if (dismissed) {
        writeFeatureDemoDismissed(storageId, 'skipped')
        setVisible(false)
        return
      }
      const palettePending = shouldShowFeatureDemo(paletteStorageId, {
        enabled: FEATURE_FLAGS.commandPaletteOnboarding,
      })
      setVisible(shouldShowFeatureDemo(storageId, { enabled: enabled && !palettePending }))
    })()
    return () => {
      cancelled = true
    }
  }, [ready, anchorEl, enabled, storageId, paletteStorageId, uid, defer, inAudience])

  const step = demoIndex === null ? null : DEMO_STEPS[demoIndex]
  const stepLabel = useMemo(() => {
    if (demoIndex === null) return 'New'
    return `Step ${demoIndex + 1} of ${DEMO_STEPS.length}`
  }, [demoIndex])

  useEffect(() => {
    if (!step) return undefined
    openedByDemo.current = true
    onOpenAssistant()
    const detail: AssistantDemoDetail = {
      history: Boolean(step.history),
      workspace: Boolean(step.workspace),
      phonePane: step.phonePane,
      samplePreview: step.samplePreview ?? false,
    }
    window.dispatchEvent(new CustomEvent<AssistantDemoDetail>(ASSISTANT_DEMO_EVENT, { detail }))
    let attempts = 0
    let timer = 0
    const look = () => {
      const exact = document.querySelector(`[data-assistant-demo="${step.anchor}"]`)
      if (exact instanceof HTMLElement) {
        exact.scrollIntoView({ block: 'nearest' })
        setStepAnchor(exact)
        return
      }
      attempts += 1
      if (attempts < 8) {
        timer = window.setTimeout(look, 120)
        return
      }
      const fallback = document.querySelector('[data-assistant-demo-anchor]')
      if (fallback instanceof HTMLElement) setStepAnchor(fallback)
    }
    timer = window.setTimeout(look, 80)
    return () => window.clearTimeout(timer)
  }, [step, onOpenAssistant])

  const dismiss = (choice: FeatureDemoChoice) => {
    writeFeatureDemoDismissed(storageId, choice)
    setVisible(false)
    setDemoIndex(null)
    setStepAnchor(null)
    if (openedByDemo.current) {
      openedByDemo.current = false
      window.dispatchEvent(
        new CustomEvent<AssistantDemoDetail>(ASSISTANT_DEMO_EVENT, { detail: { exit: true } }),
      )
      onCloseAssistant()
    }
  }

  const dismissForever = () => {
    if (uid) {
      void writeAssistantDemoDismissed(uid, PORTAL_ASSISTANT_DEMO_ID).catch(error => {
        console.warn('assistant demo dismiss failed', error)
      })
    }
    dismiss('skipped')
  }

  if (!visible || !anchorEl) return null

  const popperAnchor = step && stepAnchor ? stepAnchor : anchorEl
  const placement = step ? (isMobile ? 'bottom' : 'left-start') : isMobile ? 'bottom' : 'bottom-end'

  if (step && demoIndex !== null) {
    const isLast = demoIndex === DEMO_STEPS.length - 1
    return (
      <FeatureDemo
        open
        anchorEl={popperAnchor}
        title={step.title}
        body={isMobile && step.mobileBody ? step.mobileBody : step.body}
        stepLabel={stepLabel}
        primaryLabel={isLast ? 'Done' : 'Next'}
        secondaryLabel={demoIndex > 0 ? 'Back' : undefined}
        tertiaryLabel='Skip'
        onPrimary={() => {
          if (isLast) dismiss('completed')
          else setDemoIndex(index => (index ?? 0) + 1)
        }}
        onSecondary={demoIndex > 0 ? () => setDemoIndex(index => (index ?? 1) - 1) : undefined}
        onTertiary={() => dismiss('skipped')}
        onDismissForever={dismissForever}
        placement={placement}
      />
    )
  }

  return (
    <FeatureDemo
      open
      anchorEl={anchorEl}
      title={INTRO.title}
      body={isMobile ? INTRO.mobileBody : INTRO.desktopBody}
      stepLabel='New'
      primaryLabel='Show Me'
      secondaryLabel='Got It'
      onPrimary={() => setDemoIndex(0)}
      onSecondary={() => dismiss('skipped')}
      onDismissForever={dismissForever}
      placement={placement}
    />
  )
}
